import { useEffect, useState } from "react";
import useI18n from "@/hooks/useI18n";
import {
  ScraperConsultationItem,
  ScrapersConsultation,
} from "@/domain/scrapers/consultation";
import ScraperPassagesDrawer from "@/atomic/molecules/ScraperPassagesDrawer";

interface ScrapersConsultationCardProps {
  consultation: ScrapersConsultation;
}

function ConsultingSpinner() {
  const { t } = useI18n();

  return (
    <span
      role="status"
      aria-label={t("common:spinner.wait")}
      className="shrink-0 w-6 h-6"
    >
      <img
        src="/svg/icon-loading.svg"
        alt=""
        width={24}
        height={24}
        className="animate-spin w-6 h-6"
        aria-hidden="true"
      />
    </span>
  );
}

function ChevronIcon({ direction }: { direction: "up" | "down" }) {
  const path =
    direction === "up"
      ? "M18.9999 15.8C18.7999 15.8 18.5999 15.7 18.4999 15.6L12.2999 8.89995C12.0999 8.69995 11.9999 8.69995 11.7999 8.89995L5.5999 15.6C5.2999 15.9 4.7999 15.9 4.4999 15.6C4.1999 15.3 4.1999 14.8 4.4999 14.5L10.6999 7.79995C11.3999 6.99995 12.5999 6.99995 13.2999 7.79995L19.4999 14.5C19.7999 15.3 19.4999 15.6C19.3999 15.7 19.1999 15.8 18.9999 15.8Z"
      : "M11.9998 16.8C11.4998 16.8 10.9998 16.6001 10.6998 16.2001L4.3998 9.50005C4.0998 9.20005 4.0998 8.70005 4.3998 8.40005C4.6998 8.10005 5.1998 8.10005 5.4998 8.40005L11.6998 15.1C11.8998 15.3 11.9998 15.3 12.1998 15.1L18.3998 8.40005C18.6998 8.10005 19.1998 8.10005 19.4998 8.40005C19.7998 8.70005 19.7998 9.20005 19.4998 9.50005L13.2998 16.2001C12.9998 16.6001 12.4998 16.8 11.9998 16.8Z";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className="shrink-0 w-6 h-6 text-text"
      aria-hidden="true"
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}

function PendingSkeleton() {
  return (
    <span
      data-testid="scraper-pending-skeleton"
      aria-hidden="true"
      className="w-6 h-6 rounded bg-border shrink-0"
    />
  );
}

function StatusBadge({ item }: { item: ScraperConsultationItem }) {
  const { t } = useI18n();

  if (item.status === "with_passages") {
    return (
      <span className="px-1 min-h-4 rounded bg-info-muted text-info-muted-text text-xs font-medium font-secondary leading-[16.8px] flex items-center">
        {t("pages:home:scrapersPassageCount", { count: item.passageCount })}
      </span>
    );
  }

  if (item.status === "error") {
    return (
      <span className="text-xs font-medium font-secondary text-error leading-[16.8px] tracking-[-0.4px]">
        {t("pages:home:scrapersItemError")}
      </span>
    );
  }

  if (item.status === "pending") {
    return (
      <span className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-medium font-secondary text-text tracking-[-0.4px] leading-[18.2px]">
          {t("pages:home:scrapersItemConsulting")}
        </span>
        <ConsultingSpinner />
      </span>
    );
  }

  return (
    <span className="px-1 h-4 rounded bg-success-muted text-success-muted-text text-xs font-medium font-secondary leading-[16.8px] flex items-center">
      {t("pages:home:scrapersItemEmpty")}
    </span>
  );
}

function ConcessionaireRow({
  item,
  isSelected,
  onSelect,
}: {
  item: ScraperConsultationItem;
  isSelected: boolean;
  onSelect: (item: ScraperConsultationItem) => void;
}) {
  const { t } = useI18n();
  const isClickable = item.status === "with_passages";
  const rowClass = `w-full text-left rounded-2xl p-5 border ${
    isSelected ? "border-primary" : "border-border"
  }`;

  const content = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        {item.status === "pending" && <PendingSkeleton />}
        <p className="flex-1 min-w-0 text-base font-medium font-secondary text-text tracking-[-0.4px] leading-[20.8px] text-left">
          {item.name}
        </p>
        <StatusBadge item={item} />
      </div>
      {item.status === "with_passages" && item.totalPrice && (
        <div className="mt-2 flex items-center justify-between w-full">
          <p className="text-sm font-medium font-secondary text-tertiary tracking-[-0.4px] leading-[18.2px]">
            {t("pages:home:scrapersTotalToPay")}
          </p>
          <p className="text-sm font-medium font-secondary text-text tracking-[-0.4px] leading-[18.2px]">
            R$ {item.totalPrice}
          </p>
        </div>
      )}
    </>
  );

  if (!isClickable) {
    return <div className={rowClass}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      aria-pressed={isSelected}
      className={rowClass}
    >
      {content}
    </button>
  );
}

export default function ScrapersConsultationCard({
  consultation,
}: ScrapersConsultationCardProps) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(!consultation.isComplete);
  const [selectedItem, setSelectedItem] =
    useState<ScraperConsultationItem | null>(null);

  const title = consultation.isComplete
    ? t("pages:home:scrapersConsultedTitle")
    : t("pages:home:scrapersConsultingTitle");

  useEffect(() => {
    if (!consultation.isComplete) {
      setIsOpen(true);
      return;
    }
    if (consultation.hasAnyPassage || consultation.hasConsultationError) {
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
  }, [
    consultation.isComplete,
    consultation.hasAnyPassage,
    consultation.hasConsultationError,
  ]);

  const handleToggle = () => {
    setIsOpen((current) => !current);
  };

  return (
    <div className="w-full bg-foreground border border-border rounded-2xl p-5">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-busy={!consultation.isComplete}
        className="w-full flex items-center gap-2 text-left"
      >
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <p className="text-base font-medium font-secondary text-tertiary tracking-[-0.4px] leading-[20.8px]">
            {title}
          </p>
          {!consultation.isComplete && !consultation.hasAnyPassage && (
            <p className="text-sm font-secondary text-text-secondary tracking-[-0.4px] leading-[18.2px]">
              {t("pages:home:scrapersProgress", {
                completed: consultation.completedCount,
                total: consultation.total,
              })}
            </p>
          )}
          {consultation.isComplete && consultation.hasConsultationError && (
            <p className="text-sm font-secondary text-text-secondary tracking-[-0.4px] leading-[18.2px]">
              {t("pages:home:scrapersItemError")}
            </p>
          )}
          {consultation.hasAnyPassage && !consultation.hasConsultationError && (
            <p className="text-sm font-secondary text-text-secondary tracking-[-0.4px] leading-[18.2px]">
              {t("pages:home:scrapersFoundPassages", {
                count: consultation.totalPassageCount,
              })}
            </p>
          )}
          {consultation.isComplete &&
            !consultation.hasAnyPassage &&
            !consultation.hasConsultationError && (
              <span className="self-start px-2 min-h-6 rounded bg-success-muted text-success-muted-text text-sm font-medium font-secondary leading-[18.2px] inline-flex items-center">
                {t("pages:home:scrapersNoPassagesBadge")}
              </span>
            )}
        </div>
        <ChevronIcon direction={isOpen ? "up" : "down"} />
      </button>

      {isOpen && (
        <ul className="mt-3 flex flex-col gap-3">
          {consultation.items.map((item) => (
            <li key={item.id}>
              <ConcessionaireRow
                item={item}
                isSelected={selectedItem?.id === item.id}
                onSelect={setSelectedItem}
              />
            </li>
          ))}
        </ul>
      )}

      <ScraperPassagesDrawer
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
}
